import { useCallback, useMemo } from 'react'
import { DataTable } from '@/components/data-table'
import { useDataTable } from '@/hooks/use-data-table'
import { useIntlayer } from 'react-intlayer'
import { useSelector } from '@/libs/state'
import { session$ } from '@/features/auth/store'
import { usePermission } from '@/features/permissions/hooks/use-permission'
import { useOwnTimeOffRequests } from '../hooks/use-own-time-off-requests'
import { useTeamTimeOffRequests } from '../hooks/use-team-time-off-requests'
import { useMembers } from '../hooks/use-members'
import { useDeptMemberIds } from '../hooks/use-dept-member-ids'
import { useTimeOffColumns, useTimeOffOwnColumns } from './time-off-columns'
import type { TimeOffTableMeta } from './time-off-columns'
import { TimeOffHeader } from './time-off-header'
import { TimeOffBalanceSummary } from './time-off-balance-summary'
import { TimeOffRequestSheet } from './time-off-request-sheet'
import { TimeOffDetailSheet } from './time-off-detail-sheet'
import { useMemberParams } from './use-member-params'
import { PageLayoutContainer } from '@/components/page-layout'

const sortableColumns = new Set(['member', 'type', 'dates', 'status'])

const TimeOffPage = () => {
	const canViewOwn = usePermission('hr.time-off.view-own')
	const canViewTeam = usePermission('hr.time-off.view-team')
	const canViewAll = usePermission('hr.time-off.view-all')
	const user = useSelector(() => session$.user.get())
	const content = useIntlayer('hr-page')
	const { q, toStage, toType, sortBy, sortDir, setParams } = useMemberParams()
	const { members } = useMembers(null, null)

	const ownMemberId = useMemo(
		() => members.find(member => member.email === user?.email)?.id ?? null,
		[members, user?.email],
	)

	const avatarByMemberId = useMemo(() => {
		const map = new Map<string, string>()
		for (const member of members) {
			if (member.avatarUrl) map.set(member.id, member.avatarUrl)
		}
		return map
	}, [members])

	const { memberIds: deptMemberIds } = useDeptMemberIds(canViewTeam && !canViewAll ? ownMemberId : null)

	const teamMemberIds = useMemo(() => {
		if (canViewAll) return null
		if (canViewTeam) return deptMemberIds.length > 0 ? deptMemberIds : null
		return null
	}, [canViewAll, canViewTeam, deptMemberIds])

	const { requests: ownRequests } = useOwnTimeOffRequests(ownMemberId ?? '__no-member__')
	const { requests: teamRequests } = useTeamTimeOffRequests(
		q ?? null,
		teamMemberIds,
		ownMemberId,
		toStage ?? null,
		toType ?? null,
	)

	const meta: TimeOffTableMeta = { onView: id => setParams({ viewTimeOff: id }) }

	const ownColumns = useTimeOffOwnColumns()
	const teamColumns = useTimeOffColumns(avatarByMemberId)

	const { table: ownTable } = useDataTable({ data: ownRequests, columns: ownColumns, getRowId: row => row.id, meta })
	const { table: teamTable } = useDataTable({ data: teamRequests, columns: teamColumns, getRowId: row => row.id, meta })

	const handleSort = useCallback(
		(key: string) => {
			if (sortBy !== key) return setParams({ sortBy: key, sortDir: 'asc' })
			if (sortDir === 'asc') return setParams({ sortBy: key, sortDir: 'desc' })
			setParams({ sortBy: null, sortDir: null })
		},
		[sortBy, sortDir, setParams],
	)

	const to = content.timeOff
	const showTeamSection = canViewTeam || canViewAll
	const hasFilters = Boolean(q || toStage || toType)

	if (!canViewOwn) return null

	return (
		<PageLayoutContainer>
			<TimeOffBalanceSummary />

			<div className='flex flex-col gap-3'>
				{ownRequests.length === 0 ? (
					<p className='text-sm text-muted-foreground py-4'>{to.myRequests.empty}</p>
				) : (
					<DataTable
						table={ownTable}
						sortBy={null}
						sortDir={null}
						sortableColumns={new Set()}
						onSort={() => undefined}
						onRowClick={id => setParams({ viewTimeOff: id })}
					/>
				)}
			</div>

			{showTeamSection && (
				<>
					<div className='border-t border-border' />
					<div className='flex flex-col gap-3'>
						<TimeOffHeader />
						{teamRequests.length === 0 ? (
							<div className='flex flex-col items-center justify-center py-16 text-center'>
								<p className='text-base font-medium'>{hasFilters ? to.noResults.title : to.empty.title}</p>
								<p className='text-sm text-muted-foreground mt-1'>
									{hasFilters ? to.noResults.description : to.empty.description}
								</p>
							</div>
						) : (
							<DataTable
								table={teamTable}
								sortBy={sortBy}
								sortDir={sortDir}
								sortableColumns={sortableColumns}
								onSort={handleSort}
								onRowClick={id => setParams({ viewTimeOff: id })}
							/>
						)}
					</div>
				</>
			)}

			<TimeOffRequestSheet />
			<TimeOffDetailSheet />
		</PageLayoutContainer>
	)
}

export { TimeOffPage }
